import { Config } from 'config/envConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';
import {
    getFileFormat,
    getFileIcon,
    getFileIconColor,
    isImageFile,
    isMarkdownFile,
    ProcessType,
    TaskProgressType,
} from '@quyva/common';
import { getUrl } from '../utils';
import { getFastApiUrl } from '../utils/getUrl';
import { developerMode } from '../Constants';
import {
    Modal,
    Dropdown,
    ConfirmModal,
    alert,
    AlertType,
    Button,
} from '@quyva/shared-ui';
import {
    faArrowUpRightFromSquare,
    faBugSlash,
    faComments,
    faFile,
    faGlobe,
    faTrash,
    faEllipsisV,
    faSyncAlt,
    faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { useGlobalCredits } from '../context/GlobalStateContext';
import { DebugViewerContainer } from './debugViewerContainer';
import { RepoType } from '@quyva/common';
import axios from 'axios';
import { message } from 'antd';

export const FilesListView = ({
    repo,
    allReposFilesData,
    setWorkOnRepo,
    repoId,
    email,
    chatrepofunction,
    folderName,
    workOnRepo,
    mode,
    fileToChat,
    setFileToChat,
    isAdmin,
    closeSidebar,
    togglePdfVisibility,
    getRepoFilesData,
    setIsLoading,
    isRepoSortedAlphabetically,
    fileName,
    setFileName,
    showDebugModal,
    setShowDebugModal,
    debugModalRepoId,
    setDebugModalRepoId,
    socket,
    processingRepos,
    currentUserHasAdminRightsOnRepo,
    isSharedToCurrentUser
}) => {
    const [closeDropdownSignal, setCloseDropdownSignal] = useState(false);
    const { transactionMode, setTransactionMode } = useGlobalCredits();
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showRecrawlModal, setShowRecrawlModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [urlFileMap, setUrlFileMap] = useState({});

    const canDeleteFiles = !isSharedToCurrentUser || currentUserHasAdminRightsOnRepo;

    const BREAKPOINT_WIDTH = 1024;

    const repoFileData =
        allReposFilesData && allReposFilesData.size
            ? allReposFilesData.get(repoId)
            : null;

    const fetchUrlInfo = async (repoId) => {
        try {
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/crawl/url_file_map?repo_id=${repoId}`
            );
            const response = await axios.get(dynamicUrl);
            return response.data; // Assuming the response is a mapping of URLs
        } catch (error) {
            console.error('Error fetching URL info:', error);
            return {};
        }
    };

    useEffect(() => {
        if (
            repoFileData?.isExpanded &&
            repo &&
            repo.storage_type === RepoType.INTERNET
        ) {
            const fetchUrls = async () => {
                // Fetch URL info for each repo individually
                const urls = await fetchUrlInfo(repoId);
                // Merge the results into the allUrlInfo object
                setUrlFileMap(urls);
            };

            if (repoId) {
                fetchUrls();
            }
        }
    }, [repoId, repoFileData?.isExpanded]);

    // Function to handle confirmed deletion
    const handleDeleteFile = async () => {
        if (!fileToDelete) return;

        try {
            setShowConfirmModal(false);
            setIsLoading(true);
            const deleteApiUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/delete-file/`
            );

            const payload = {
                repo_id: repoId,
                file_name: fileToDelete.name,
                repo_name: repo.name,
                email: email,
                is_non_db_repo:
                    fileToDelete.repotype !== RepoType.SQLLITE &&
                    fileToDelete.repotype !== RepoType.POSTGRESS &&
                    fileToDelete.repotype !== RepoType.MSSQL,
            };

            await axios.post(deleteApiUrl, payload);
            message.success('File deleted successfully');
            setIsLoading(false);
            // Refresh the file list after deletion
            await getRepoFilesData(repoId);
        } catch (error) {
            console.error('Error deleting file:', error);
            message.error('Failed to delete the file', 2);
        } finally {
            setFileToDelete(null);
        }
    };

    const handleDownloadFile = async (file) => {
        if (!file) return;

        try {
            setIsLoading(true);
            const downloadApiUrl = getFastApiUrl('/download-file/');

            // Build form data
            const formData = new FormData();
            formData.append('file_name', file.name);
            formData.append('repo_name', repo.name);
            formData.append('email', email);

            const response = await axios.post(downloadApiUrl, formData, {
                responseType: 'blob',
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Create a blob URL and trigger download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', file.name); // force download
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url); // cleanup
            alert(AlertType.SUCCESS, 'File downloaded successfully');
        } catch (error) {
            console.error('Error downloading file:', error);
            alert(AlertType.ERROR, 'Error downloading file');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRecrawlSite = async () => {
        if (!fileToDelete) return;

        setShowRecrawlModal(false);
        try {
            socket.emit('statusUpdate', {
                message: 'Adding URL...',
                percentageCompletion: 0,
                process: ProcessType.FILE_UPLOAD,
                progress: TaskProgressType.STARTED,
                repoId: repoId,
                repoName: repo.name,
                statusCode: 201,
                timeStamp: new Date(),
                userMail: email,
            });
            // Prepare crawl request payload
            const requestBody = {
                repoName: repo.name,
                user: email,
                urls: [urlFileMap[fileToDelete.name]?.url],
                autoRecrawl: urlFileMap[fileToDelete.name]?.auto_recrawl,
                recrawlInterval:
                    urlFileMap[fileToDelete.name]?.recrawl_interval_days,
                fileUpload: true,
                // Server-side atomic replace: the backend deletes this old file
                // only after the new crawl for the URL succeeds (old kept as
                // backup on failure). No client-side socket-driven delete needed.
                replaceFileName: fileToDelete.name,
            };
            const user = JSON.parse(localStorage.getItem('current'));
            // Make the POST request
            const crawlResponse = await fetch(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/crawl/crawl_and_store`
                ),
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: encodeURIComponent(user.token),
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            if (!crawlResponse.ok) {
                setFileToDelete(null);
                throw new Error('Recrawl process failed. Please try again.');
            }
        } catch (error) {
            setFileToDelete(null);
            console.error('Recrawl failed:', error);
            message.error('Failed to recrawl the website');
        }
    };
    if (repoFileData) {
        const isExpanded = repoFileData.isExpanded;
        const filesData = repoFileData.filesData;

        // Sort files based on isSortedRepo
        const sortedFilesData = isRepoSortedAlphabetically
            ? [...filesData].sort((a, b) => a.name.localeCompare(b.name))
            : filesData;

        const visibleFilesData = sortedFilesData.filter((item) => {
            if (repo.storage_type !== RepoType.INTERNET) {
                return true;
            }

            // associated_website holds the source URL on the primary per-URL
            // summary file, but holds the parent file's *name* on sub-resources
            // (attachments/pages extracted from that URL) - only hide the latter.
            const associatedWebsite = item?.associated_website;
            const isSubResource =
                !!associatedWebsite && !/^https?:\/\//i.test(associatedWebsite);
            return !isSubResource;
        });

        const list = visibleFilesData.map((item, index) => {
            const extension = getFileFormat(item.name);
            const isImage = isImageFile(extension);

            // Get the URL for the current file
            const fileUrl = urlFileMap[item.name]?.url; // Assuming urlFileMap contains the mapping
            const isUrlBackedItem = !!fileUrl;
            // Define dropdown options dynamically
            let dropDownList = [];

            if (developerMode && isAdmin && !isImage) {
                dropDownList.push({
                    label:
                        isUrlBackedItem
                            ? 'Debug website content'
                            : 'Debug File',
                    icon: faBugSlash,
                    // iconColor: 'blue',
                    action: () => {
                        setFileName(item.name);
                        setDebugModalRepoId(repoId);
                        setShowDebugModal(true);
                    },
                    disabled: false,
                    type: 'success',
                });
            } else {
                dropDownList.push({
                    label:
                        isUrlBackedItem
                            ? 'View website content'
                            : 'View File',
                    icon: faArrowUpRightFromSquare,
                    // iconColor: 'green',
                    action: () => {
                        setFileName(item.name);
                        setDebugModalRepoId(repoId);
                        setShowDebugModal(true);
                    },
                    disabled: false,
                    type: 'success',
                });
            }

            if (!(item.name.endsWith('.db') || mode === 'info')) {
                dropDownList.push({
                    label:
                        isUrlBackedItem
                            ? 'Chat with the website'
                            : 'Chat with the file',
                    icon: faComments,
                    // iconColor: 'purple',
                    action: () => {
                        if (transactionMode) {
                            setTransactionMode(!transactionMode);
                        }
                        setFileToChat(item.name);
                        setWorkOnRepo(repoId);
                        chatrepofunction(repo);
                        if (window.innerWidth < BREAKPOINT_WIDTH) {
                            togglePdfVisibility('reset');
                            closeSidebar();
                        }
                    },
                    disabled: false,
                    type: 'success',
                });
            }

            if (!item.name.toLowerCase().endsWith('.db')) {
                dropDownList.push({
                    label: 'Download File',
                    icon: faDownload,
                    action: () => {
                        handleDownloadFile(item);
                    },
                    hoverText: 'Download this file to your device',
                    disabled: false,
                    type: 'success',
                });
            }

            // Delete action
            dropDownList.push({
                label:
                    isUrlBackedItem
                        ? 'Delete the website'
                        : 'Delete File',
                icon: faTrash,
                // iconColor: 'red',
                action: () => {
                    setFileToDelete(item); // Store file details for deletion
                    setShowConfirmModal(true); // Show confirmation modal
                },
                disabled: !canDeleteFiles,
                type: 'danger',
            });
            // Recrawl action
            if (isUrlBackedItem) {
                dropDownList.push({
                    label: 'Recrawl the website',
                    icon: faSyncAlt,
                    action: () => {
                        setFileToDelete(item); // Store file details for deletion
                        setShowRecrawlModal(true); // Show confirmation modal
                    },
                    disabled: !!processingRepos.length, // convert length to boolean
                    hoverText: processingRepos.length
                        ? 'Disabled as a heavy task is under progress'
                        : 'Recrawl this Website',
                    type: 'success',
                });
            }

            return (
                <div
                    key={`${item.document_id} ${index}`}
                    className='flex justify-between items-center'
                >
                    <div className='h-10 truncate border-l-2 pr-2 flex items-center'>
                        <span
                            className='mr-2 font-bold'
                            style={{ color: '#e6e6e8' }}
                        >
                            -
                        </span>
                        <FontAwesomeIcon
                            icon={isUrlBackedItem ? faGlobe : getFileIcon(item.name)}
                            color={isUrlBackedItem ? '#2563eb' : getFileIconColor(item.name)}
                            className='mr-2 drop-shadow-sm'
                        />
                        <span
                            className='truncate text-base'
                            title={fileUrl || item.name} // Show URL / file name on hover
                        >
                            {fileUrl ? (
                                <span className='text-blue-600 underline'>
                                    {fileUrl}
                                </span>
                            ) : (
                                item.name
                            )}
                        </span>
                    </div>
                    <div className='h-10 flex items-center'>
                        {item.name.endsWith('.db') ? null : (
                            <Dropdown
                                dropDownList={dropDownList}
                                icon={<FontAwesomeIcon icon={faEllipsisV} />}
                                showButtonText={false}
                                buttonText={null}
                                buttonHoverText={'More File Actions'}
                                additionalButtonAction={() => {
                                    setCloseDropdownSignal(false);
                                }}
                                titleText={'File Actions'}
                                buttonClassName={'px-3.5 py-2 ml-2'}
                                backgroundColor={'white'}
                                textColor={'green'}
                                placement={'right'}
                                closeSignal={closeDropdownSignal}
                            />
                        )}
                    </div>
                </div>
            );
        });

        const extension = getFileFormat(fileName);
        const isImage = isImageFile(extension);
        const isMarkdown = isMarkdownFile(extension);
        const websiteUrl = urlFileMap[fileName]?.url ?? null;

        if (isExpanded) {
            return (
                <div>
                    <div
                        className={`${
                            workOnRepo === repoId
                                ? 'bg-backgrounds-primary100'
                                : 'bg-backgrounds-white'
                        } rounded-b-xl pl-4 pr-2 pb-3 truncate shadow-sm border`}
                    >
                        {list}
                    </div>
                    {showConfirmModal && (
                        <ConfirmModal
                            modalText={`Are you sure you want to delete "${
                                repo.storage_type === RepoType.INTERNET
                                    ? urlFileMap?.[fileToDelete?.name]?.url ??
                                      fileToDelete?.name ??
                                      'this item'
                                    : fileToDelete?.name ?? 'this item'
                            }"?`}
                            onConfirm={handleDeleteFile}
                            onCancel={() => setShowConfirmModal(false)}
                        />
                    )}

                    {showRecrawlModal && (
                        <ConfirmModal
                            modalText={`Are you sure you want to recrawl the site "${
                                urlFileMap?.[fileToDelete?.name]?.url ??
                                fileToDelete?.name ??
                                'this item'
                            }"?`}
                            onConfirm={handleRecrawlSite}
                            onCancel={() => setShowRecrawlModal(false)}
                        />
                    )}

                    {/* Debug Modal */}
                    {showDebugModal && debugModalRepoId === repoId && (
                        <Modal
                            title={
                                isAdmin && !isImage && !isMarkdown
                                    ? 'Debug Diagnostics'
                                    : 'File viewer'
                            }
                            titleIcon={
                                isAdmin && !isImage ? faBugSlash : faFile
                            }
                            iconColor={'sky'}
                            onClose={() => {
                                setShowDebugModal(false);
                                setDebugModalRepoId(null);
                            }}
                            className={''}
                            zIndex={9999}
                        >
                            <DebugViewerContainer
                                repoId={repoId}
                                email={email}
                                folderName={folderName}
                                fileName={fileName}
                                websiteUrl={websiteUrl}
                                mode={mode}
                                isAdmin={isAdmin}
                            />
                        </Modal>
                    )}
                </div>
            );
        }

        return <></>;
    }
};
